import { transformIndividualResourceEntry } from 'layers/content/v3/transforms/transformIndividualResourceEntry'

const toLessonTitleFromSlug = slug => {
  if (!slug || typeof slug !== 'string') {
    return ''
  }

  return slug
    .split('-')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const getFirstTruthyValue = values => {
  return values.find(Boolean) || null
}

const PROPERTY_TO_FACET_ID = {
  resourceType: 'metadataResourceType',
  duration: 'metadataDuration',
  frequency: 'metadataFrequency',
  grade: 'metadataGrade',
  audience: 'metadataAudience',
  unit: 'metadataUnit',
  goal: 'metadataGoal',
  learningCondition: 'metadataLearningCondition',
  academicSkill: 'metadataAcademicSkills',
  caselCompetency: 'metadataCasel',
  materials: 'metadataMaterials',
  programTheme: 'metadataProgramThemes',
}

const getCanonicalFacetId = ({ facetId, facetMappings }) => {
  if (!facetId || typeof facetId !== 'string') {
    return null
  }

  const facetIdByProperty = Object.values(PROPERTY_TO_FACET_ID)
  const lowerFirstFacetId = `${facetId.charAt(0).toLowerCase()}${facetId.slice(
    1,
  )}`
  const candidates = [facetId, lowerFirstFacetId]

  return (
    candidates.find(
      candidate =>
        !!facetMappings.byFacetId[candidate] || facetIdByProperty.includes(candidate),
    ) || null
  )
}

const toSnakeCase = value => {
  return String(value || '')
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
}

const getFacetMappings = facetMetadataSetting => {
  const facets = [
    ...(facetMetadataSetting?.column1Facets || []),
    ...(facetMetadataSetting?.column2Facets || []),
  ]
  const byFacetId = {}
  const byProperty = {}

  facets.forEach(facet => {
    if (facet?.facetId && facet?.facetPropertyName) {
      byFacetId[facet.facetId] = facet.facetPropertyName
      byProperty[facet.facetPropertyName] = facet.facetId
    }
  })

  return { byFacetId, byProperty }
}

const getMetadataPropertyName = ({ metadataValue, facetId, facetMappings }) => {
  if (!metadataValue || typeof metadataValue !== 'object') {
    return ''
  }

  const mappedPropertyName = facetMappings.byFacetId[facetId]
  if (mappedPropertyName) {
    return mappedPropertyName
  }

  const ignoredKeys = new Set([
    'contentType',
    'facetId',
    'facet_id',
    'value',
    'label',
    'displayValue',
    'display_value',
    'id',
    'type',
    'locale',
  ])
  const candidatePropertyName = Object.keys(metadataValue).find(
    key => !ignoredKeys.has(key),
  )

  return candidatePropertyName || ''
}

const normalizeMetadataValue = ({ metadataValue, facetMappings }) => {
  const rawFacetId = getFirstTruthyValue([
    metadataValue?.contentType?.id,
    typeof metadataValue?.contentType === 'string'
      ? metadataValue.contentType
      : null,
    metadataValue?.facetId,
    metadataValue?.facet_id,
  ])
  const facetId = getCanonicalFacetId({
    facetId: rawFacetId,
    facetMappings,
  })

  const propertyName = getMetadataPropertyName({
    metadataValue,
    facetId,
    facetMappings,
  })
  const resolvedFacetId =
    facetId ||
    facetMappings.byProperty[propertyName] ||
    PROPERTY_TO_FACET_ID[propertyName] ||
    rawFacetId ||
    null

  if (!propertyName && !resolvedFacetId) {
    return metadataValue
  }

  const resolvedPropertyName =
    propertyName ||
    facetMappings.byFacetId[resolvedFacetId] ||
    Object.keys(PROPERTY_TO_FACET_ID).find(
      key => PROPERTY_TO_FACET_ID[key] === resolvedFacetId,
    ) ||
    'value'

  const value = getFirstTruthyValue([
    metadataValue?.[resolvedPropertyName],
    metadataValue?.[toSnakeCase(resolvedPropertyName)],
    metadataValue?.value,
    metadataValue?.displayValue,
    metadataValue?.display_value,
  ])

  if (value == null) {
    return metadataValue
  }

  return {
    ...metadataValue,
    contentType: {
      id: resolvedFacetId || metadataValue?.contentType?.id || '',
    },
    [resolvedPropertyName]: value,
  }
}

const buildMetadataValuesFromResourceMetadata = ({
  resourceMetadata,
  facetMetadataSetting,
}) => {
  const facets = [
    ...(facetMetadataSetting?.column1Facets || []),
    ...(facetMetadataSetting?.column2Facets || []),
  ]

  return facets
    .map(facet => {
      const value = getFirstTruthyValue([
        resourceMetadata?.[facet.facetPropertyName],
        resourceMetadata?.[toSnakeCase(facet.facetPropertyName)],
      ])

      if (value == null) {
        return null
      }

      return {
        contentType: {
          id: facet.facetId,
        },
        [facet.facetPropertyName]: value,
      }
    })
    .filter(Boolean)
}

const normalizeResourceMetadata = ({ entry, resourceMetadata }) => {
  const facetMetadataSetting =
    entry?.facetMetadataSetting || entry?.facet_metadata_setting || {}
  const facetMappings = getFacetMappings(facetMetadataSetting)
  const routeSlug = getFirstTruthyValue([
    resourceMetadata?.routeSlug,
    resourceMetadata?.route_slug,
    entry?.routeSlug,
    entry?.route_slug,
    entry?.slug,
  ])

  const displayTitle =
    getFirstTruthyValue([
      resourceMetadata?.displayTitle,
      resourceMetadata?.display_title,
      entry?.displayTitle,
      entry?.display_title,
      entry?.title,
      entry?.internalTitle,
    ]) || toLessonTitleFromSlug(routeSlug)

  const description = getFirstTruthyValue([
    resourceMetadata?.description,
    entry?.description,
    entry?.summary,
  ])

  const rawMetadataValues = getFirstTruthyValue([
    Array.isArray(resourceMetadata?.metadataValues) &&
      resourceMetadata.metadataValues.length
      ? resourceMetadata.metadataValues
      : null,
    Array.isArray(resourceMetadata?.metadata_values) &&
      resourceMetadata.metadata_values.length
      ? resourceMetadata.metadata_values
      : null,
    Array.isArray(entry?.metadataValues) && entry.metadataValues.length
      ? entry.metadataValues
      : null,
    Array.isArray(entry?.metadata_values) && entry.metadata_values.length
      ? entry.metadata_values
      : null,
    Array.isArray(entry?.metadata) && entry.metadata.length ? entry.metadata : null,
  ]) || []
  const normalizedMetadataValues = rawMetadataValues
    .map(metadataValue => normalizeMetadataValue({ metadataValue, facetMappings }))
    .filter(Boolean)
  const fallbackMetadataValues = buildMetadataValuesFromResourceMetadata({
    resourceMetadata,
    facetMetadataSetting,
  })
  const metadataValues = normalizedMetadataValues.length
    ? normalizedMetadataValues
    : fallbackMetadataValues

  return {
    ...resourceMetadata,
    routeSlug,
    displayTitle,
    description,
    metadataValues,
    courseNodeConnector:
      resourceMetadata?.courseNodeConnector ||
      resourceMetadata?.course_node_connector ||
      entry?.courseNodeConnector ||
      {},
  }
}

const normalizeLegacyEntry = entry => {
  const resourceMetadata = normalizeResourceMetadata({
    entry,
    resourceMetadata: entry?.resourceMetadata || {},
  })
  const normalizedEntry = {
    ...entry,
    displayTitle:
      entry?.displayTitle || entry?.display_title || resourceMetadata.displayTitle,
    description:
      entry?.description || entry?.summary || resourceMetadata.description,
    routeSlug: entry?.routeSlug || entry?.route_slug || resourceMetadata.routeSlug,
    facetMetadataSetting:
      entry?.facetMetadataSetting || entry?.facet_metadata_setting,
    resourceMetadata,
  }

  const hasNonEmptyCanonicalActivityDetails =
    Array.isArray(normalizedEntry.activityDetails) &&
    normalizedEntry.activityDetails.length > 0
  const hasNonEmptySnakeCaseActivityDetails =
    Array.isArray(normalizedEntry.activity_details) &&
    normalizedEntry.activity_details.length > 0

  if (!hasNonEmptyCanonicalActivityDetails && hasNonEmptySnakeCaseActivityDetails) {
    normalizedEntry.activityDetails = normalizedEntry.activity_details
  }

  // Allow v3 transformer fallbacks to sections/content when legacy payload
  // includes an explicitly empty activityDetails array.
  if (
    Array.isArray(normalizedEntry.activityDetails) &&
    normalizedEntry.activityDetails.length === 0
  ) {
    delete normalizedEntry.activityDetails
  }

  return normalizedEntry
}

export const transformLegacyEntryForV2_5View = ({ entry }) => {
  if (!entry) {
    return null
  }

  return transformIndividualResourceEntry(normalizeLegacyEntry(entry))
}
